/**
 * Modul „House of Brückner - Royalty“ (`loyalty`): Backend-Verwaltung des Treueprogramms (Team-Sicht), API `/api/loyalty/v1/*`.
 * #854 Phase 1: nur Status. #856 Phase 2a (ADR 006): Regelwerk (versioniert, nie überschrieben), Konten 1:1 zu
 * `kunden.person`, Stufenhistorie, Royal-Einladung/-Entzug. Keine Buchungen (Crowns/Umsatz/Vorteile: Phase 2b/2c).
 * Begriffe: Stufen (Friend … Royal), Punkte = Crowns (Briefing 04.11.2026).
 */

export const LOYALTY_API = '/api/loyalty/v1';

/** `GET /api/loyalty/v1/status` — Modul-Gate loyalty + Cerbos `loyalty_konto` Aktion `lesen` (id "status") */
export interface LoyaltyStatus {
  modul: 'loyalty';
  phase: '2a';
  /** Anzahl Konten (aktiv, nicht beendet) */
  konten: number;
  /** aktive Regelwerk-Version (null = keine) */
  regelwerk: number | null;
}

export const VORTEIL_ARTEN = ['freischaltung', 'gutschein', 'geschenk', 'beigabe', 'zaehler', 'event', 'service', 'info'] as const;
export type VorteilArt = (typeof VORTEIL_ARTEN)[number];
export const VORTEIL_ZEITRAEUME = ['statusjahr', 'bestellung', 'einmalig', 'aufstieg'] as const;
export type VorteilZeitraum = (typeof VORTEIL_ZEITRAEUME)[number];

export interface LoyaltyStufe {
  nr: number;
  name: string;
  /** € Umsatz im Statusjahr; null = nur auf Einladung */
  schwelleEur: number | null;
  crownsJeEur: number;
  keinVerfall: boolean;
  nurEinladung: boolean;
  offen: boolean;
  hinweis: string | null;
}

export interface LoyaltyVorteil {
  /** Briefing-Nr., z. B. „3.02“ */
  code: string;
  stufeAb: number;
  titel: string;
  regel: string | null;
  art: VorteilArt;
  mengeJeZeitraum: number | null;
  zeitraum: VorteilZeitraum | null;
  ersetztCode: string | null;
  offen: boolean;
}

export interface LoyaltyBonusAktion {
  code: string;
  titel: string;
  crowns: number;
  /** zweiter Wert laut Briefing (z. B. 500/250) — Bedeutung im Hinweis */
  crownsZusatz: number | null;
  /** wiederkehrende Gutschrift je Statusjahr (Duftprofil +50 jährlich) */
  crownsJaehrlich: number | null;
  deckel: number | null;
  deckelZeitraum: 'monat' | 'statusjahr' | null;
  offen: boolean;
  hinweis: string | null;
}

export interface LoyaltyEinstellung {
  schluessel: string;
  wert: unknown;
  einheit: string | null;
  beschreibung: string;
  offen: boolean;
}

/** `GET /api/loyalty/v1/regelwerk` — aktive Version mit Stufen, Vorteilen, Bonus-Aktionen, Einstellungen */
export interface LoyaltyRegelwerk {
  nr: number;
  gueltigAb: string;
  kommentar: string;
  erstelltVon: string;
  erstelltAt: string;
  stufen: LoyaltyStufe[];
  vorteile: LoyaltyVorteil[];
  bonusAktionen: LoyaltyBonusAktion[];
  einstellungen: LoyaltyEinstellung[];
}

/** `PUT /api/loyalty/v1/regelwerk` — legt IMMER eine neue Version an (nr = höchste + 1); nichts wird überschrieben */
export interface LoyaltyRegelwerkNeu {
  kommentar: string;
  /** ISO-8601; Standard jetzt */
  gueltigAb?: string;
  stufen: LoyaltyStufe[];
  vorteile: LoyaltyVorteil[];
  bonusAktionen: LoyaltyBonusAktion[];
  einstellungen: LoyaltyEinstellung[];
}

export const STUFE_GRUENDE = ['start', 'qualifikation', 'aufstieg', 'einladung', 'geschenkt', 'manuell'] as const;
export type StufeGrund = (typeof STUFE_GRUENDE)[number];

export interface LoyaltyStufeZeitraum {
  id: string;
  stufeNr: number;
  stufeName: string | null;
  gueltigVon: string;
  gueltigBis: string | null;
  grund: StufeGrund;
  regelwerkNr: number;
  kommentar: string | null;
  erstelltVon: string;
  erstelltAt: string;
}

export interface LoyaltyRoyalEinladung {
  id: string;
  eingeladenAt: string;
  eingeladenVon: string;
  begruendung: string;
  gueltigBis: string;
  entzogenAt: string | null;
  entzogenVon: string | null;
  entzugBegruendung: string | null;
}

export const TEILNAHME_QUELLEN = ['filiale', 'online', 'team'] as const;
export type TeilnahmeQuelle = (typeof TEILNAHME_QUELLEN)[number];

/** Listeneintrag `GET /api/loyalty/v1/konten` */
export interface LoyaltyKontoKurz {
  id: string;
  personId: string;
  name: string | null;
  email: string | null;
  stufeNr: number;
  stufeName: string | null;
  /** Beginn des aktuellen Stufen-Zeitraums */
  stufeSeit: string;
  royal: boolean;
  teilnahmeAt: string;
}

export interface LoyaltyKontenListe {
  eintraege: LoyaltyKontoKurz[];
  seite: number;
  groesse: number;
  gesamt: number;
}

/** `GET /api/loyalty/v1/konten/:id` */
export interface LoyaltyKonto extends LoyaltyKontoKurz {
  teilnahmeQuelle: TeilnahmeQuelle;
  personalisierungAt: string | null;
  beendetAt: string | null;
  erstelltVon: string;
  erstelltAt: string;
  stufenhistorie: LoyaltyStufeZeitraum[];
  royalEinladungen: LoyaltyRoyalEinladung[];
}

/** `POST /api/loyalty/v1/konten` — Konto für eine bestehende Person; Teilnahme-Einwilligung Pflicht (Newsletter bleibt im CRM) */
export interface LoyaltyKontoNeu {
  personId: string;
  teilnahmeEinwilligung: true;
  teilnahmeQuelle: TeilnahmeQuelle;
  /** Einwilligung Personalisierung (getrennt, optional) */
  personalisierung?: boolean;
}

/** `POST /api/loyalty/v1/konten/:id/royal` (Einladung) und `DELETE …/royal` (Entzug, Body ebenfalls Pflicht) */
export interface LoyaltyRoyalAktion {
  begruendung: string;
}
